import { useEffect, useRef } from "react";
import { ArrowCounterClockwise, ArrowClockwise, LinkSimple, ListBullets, ListNumbers, TextB, TextItalic, TextUnderline, TextTSlash } from "@phosphor-icons/react";

const NOTES_PLACEHOLDER = "Click here to add any other notes or comments…";

const TOOLS = [
  { cmd: "bold", icon: TextB, label: "Bold" },
  { cmd: "italic", icon: TextItalic, label: "Italic" },
  { cmd: "underline", icon: TextUnderline, label: "Underline" },
  { cmd: "insertUnorderedList", icon: ListBullets, label: "Bulleted list" },
  { cmd: "insertOrderedList", icon: ListNumbers, label: "Numbered list" },
  { cmd: "createLink", icon: LinkSimple, label: "Add link" },
  { cmd: "removeFormat", icon: TextTSlash, label: "Clear formatting" },
  { cmd: "undo", icon: ArrowCounterClockwise, label: "Undo" },
  { cmd: "redo", icon: ArrowClockwise, label: "Redo" },
];

// Lightweight WYSIWYG for the email body; the initial HTML is set once so the caret never jumps.
export function RichEmailEditor({ initialHtml, onChange, readOnly = false }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = initialHtml || "";
  }, [initialHtml]);

  // Clicking the grey "Other notes" placeholder swaps it for an empty, normally styled paragraph.
  const clearPlaceholder = (e) => {
    const p = e.target.closest?.("p");
    if (readOnly || !p || p.textContent.trim() !== NOTES_PLACEHOLDER) return;
    const fresh = document.createElement("p");
    fresh.setAttribute("style", p.getAttribute("style").replace(/color:[^;]+;|font-style:[^;]+;/g, ""));
    fresh.appendChild(document.createElement("br"));
    p.replaceWith(fresh);
    const r = document.createRange();
    r.setStart(fresh, 0);
    r.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
    onChange(ref.current.innerHTML);
  };

  const run = (cmd) => {
    ref.current?.focus();
    if (cmd === "createLink") {
      const url = window.prompt("Link URL (https://…)");
      if (!url) return;
      document.execCommand(cmd, false, url);
    } else {
      document.execCommand(cmd, false, null);
    }
    onChange(ref.current.innerHTML);
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-white" data-testid="email-editor">
      {!readOnly && (
        <div className="sticky top-0 z-10 flex flex-wrap gap-1 border-b border-[#E2E8F0] bg-white/90 backdrop-blur px-3 py-2 rounded-t-lg">
          {TOOLS.map(({ cmd, icon: Icon, label }) => (
            <button key={cmd} type="button" title={label} aria-label={label} onMouseDown={(e) => { e.preventDefault(); run(cmd); }}
              className="h-8 w-8 grid place-items-center rounded-md text-[#334155] hover:bg-[#EEEEEE] transition-colors" data-testid={`editor-${cmd}`}>
              <Icon size={16} />
            </button>
          ))}
        </div>
      )}
      <div className="px-6 py-6 sm:px-10">
        <div
          ref={ref}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          onClick={clearPlaceholder}
          className="max-w-[680px] rounded-md outline-none focus:ring-2 focus:ring-[#0F172A]/10 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:underline"
          data-testid="email-body-editable"
        />
        <p className="max-w-[680px] mt-7 text-xs leading-[18px] text-[#64748B] font-['Helvetica',Arial,sans-serif]" data-testid="email-disclaimer">
          All figures net of VAT, from approved Xero invoices and credit notes. <b>Unreconciled</b> – all figures subject to change until month end reconciliation.
        </p>
      </div>
    </div>
  );
}
